import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { useNow } from "@/lib/hooks";
import { media } from "@/content/media";
import { WinnerSeal } from "@/components/Brand";
import { EASE } from "@/components/Motion";

// V3 — Hero « plein cadre » : la photo remplit toute la colonne de contenu,
// le texte est posé en bas à gauche SUR l'image, le sceau en haut à droite.
export const Hero3 = () => {
  const c = useContent();
  const { hero, ui } = c;
  const reduce = useReducedMotion();
  const isOpen = useNow(30000) >= new Date(c.site.openingDate).getTime();
  const T = 0.9;

  return (
    <section id="top" data-testid="hero" className="relative flex h-[100svh] min-h-[620px] flex-col justify-end overflow-hidden bg-[#0B0A0A] text-white">
      <motion.img
        src={media.hero}
        alt={hero.imageAlt}
        fetchPriority="high"
        className="photo-treat absolute inset-0 h-full w-full object-cover"
        initial={reduce ? false : { scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: EASE }}
      />
      <div className="photo-duo absolute inset-0" aria-hidden="true" />
      {/* Voile sombre : garantit la lisibilité du texte posé sur l'image */}
      <div className="absolute inset-0 bg-[#0B0A0A]/60" aria-hidden="true" />
      <div className="photo-vignette absolute inset-0" aria-hidden="true" />

      <motion.div
        initial={reduce ? false : { opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: T + 0.7, duration: 1.2, ease: EASE }}
        className="absolute end-6 top-24 z-10 md:end-12 lg:top-14"
      >
        <WinnerSeal size={104} className="md:h-[140px] md:w-[140px]" />
      </motion.div>

      <div className="relative z-10 px-6 pb-14 pt-44 md:px-12 lg:px-16 lg:pb-16">
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: T, duration: 1 }}
          className="flex flex-wrap items-center gap-x-5 gap-y-3 text-[0.75rem] uppercase leading-relaxed tracking-[0.35em] text-copper"
          data-testid="hero-edition"
        >
          <span className="h-px w-12 bg-copper" aria-hidden="true" />
          {hero.edition}
          <span className="basis-full text-white/85 normal-case tracking-[0.04em] md:basis-auto md:tracking-[0.08em]" data-testid="hero-status">{isOpen ? ui.countdown.open : hero.status}</span>
        </motion.p>
        <h1 className="mt-6 font-serif text-[2.1rem] font-medium leading-[1.08] md:text-[3rem]" aria-label={hero.brand} data-testid="hero-title">
          {hero.lines.map((l, i) => (
            <span key={l} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="block"
                initial={reduce ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, delay: T + 0.15 + i * 0.14, ease: EASE }}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: T + 0.55, duration: 1, ease: EASE }}
          className="mt-4 font-serif text-xl italic text-blush md:text-2xl"
          data-testid="hero-tagline"
        >
          {hero.tagline}
        </motion.p>
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: T + 0.75, duration: 1, ease: EASE }}
        >
          <p className="mt-5 max-w-xl text-[0.95rem] leading-relaxed text-white/85">{hero.text}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/candidater"
              data-testid="hero-apply-button"
              className="group inline-flex items-center gap-3 bg-plum px-8 py-4 text-[0.75rem] font-semibold uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-[#C8102E]"
            >
              {ui.apply}
              <ArrowUpRight size={16} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            {!isOpen && (
              <Link to="/candidater" data-testid="hero-notify-button" className="border border-white/40 px-8 py-4 text-[0.75rem] uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white/10">
                {ui.beNotified}
              </Link>
            )}
            <Link to="/contact" data-testid="hero-contact-button" className="link-line px-2 py-3 text-[0.75rem] uppercase tracking-[0.3em] text-white/90 hover:text-white">
              {ui.contactTeam}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
